import { useLazyQuery, useQuery } from '@apollo/client';
import { useState } from 'react';
import { LIST_WIDGETS, SEARCH_WIDGETS } from '../../../http/apollo-queries';
import SearchInput from '../../Search-input/Search-input';
import WidgetCard from './WidgetCard/WidgetCard';
import { WidgetLibraryProps, Widget } from './types';
import WidgetPreview from './WidgetPreview/WidgetPreview';
import { useTranslation } from 'react-i18next';
import { useTimeout } from '../../../hooks/useTimeout';
import { Button, Icon, Loading } from '../../UI';
import { useAuth } from '../../../context/Auth';
import { UpgradeSubscription } from '../../UpgradeSubscription/UpgradeSubscription';
import { useNavigate } from 'react-router';
import { IMembershipPlan, IWorkspaceWidget } from '../../../types/model';
import { useWorkspaces } from '../../../context/Workspaces';
import { Modal } from '../../Modal';
import { WidgetRequest } from '../../Widget';
import { logger } from '../../../helpers/logger';

export const WidgetLibrary = ({
  onWidgetInstall,
  onWidgetRemove,
  onCloseClick,
  category,
}: WidgetLibraryProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const {
    workspace,
    addWidgetToWorkspace,
    removeWidgetFromWorkspace,
    widgetAddLoading,
    widgetRemoveLoading,
    getWorkspaceLoading,
  } = useWorkspaces();

  const [widgetsList, setWidgetsList] = useState<Widget[]>([]);
  const [availableWidgetsList, setAvailableWidgetsList] = useState<Widget[]>(
    []
  );
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isRequestWidgetOpen, setIsRequestWidgetOpen] = useState(false);
  const [upgradePlans, setUpgradePlans] =
    useState<Partial<IMembershipPlan>[]>();

  logger.log('WidgetLibrary:workspace', workspace?.id);

  const [searchWidgets, { loading: searchWidgetsLoading }] =
    useLazyQuery(SEARCH_WIDGETS);

  const findFirstMembershipPlanForWidget = (widget) => {
    return widget.membershipPlans.items.reduce((prev, current) => {
      return prev.membershipPlan.position < current.membershipPlan.position
        ? prev
        : current;
    })!;
  };

  const { loading: listWidgetsLoading } = useQuery(LIST_WIDGETS, {
    onCompleted: ({ listWidgets: { items = [] } = {} }) => {
      logger.log('WidgetLibrary:listWidgets', items);

      const sortedWidgets = items
        .filter(
          (w) => w.membershipPlans.items.length > 0 && category === w.category
        )
        // .filter((w) => w.installable)
        .sort((a, b) => {
          const mpwA = findFirstMembershipPlanForWidget(a);
          const mpwB = findFirstMembershipPlanForWidget(b);
          if (mpwA.membershipPlan.position === mpwB.membershipPlan.position) {
            return a.name.localeCompare(b.name);
          }
          return mpwA.membershipPlan.position - mpwB.membershipPlan.position;
        });

      setWidgetsList(sortedWidgets);
      setAvailableWidgetsList(sortedWidgets);
    },
  });

  const workspaceWidgets: IWorkspaceWidget[] = workspace.workspaceWidgets.items;

  const [selectedWidgetId, setSelectedWidgetId] = useState('');
  const [isWidgetPreviewVisible, setIsWidgetPreviewVisible] = useState(false);

  // Search with debounce
  const { value, setValue } = useTimeout(() => {
    setIsRequestWidgetOpen(false);

    if (!value) {
      setWidgetsList(availableWidgetsList);
      return;
    }

    searchWidgets({ variables: { term: value } }).then(
      ({
        data: {
          searchWidgets: { items: searchedWidgets },
        },
      }) => {
        const filteredWidgets = availableWidgetsList.filter((aw) =>
          searchedWidgets.find((sw) => aw.id === sw.id)
        );

        setWidgetsList(filteredWidgets);
      }
    );
  }, 500);

  // Install widget
  const handleInstallClick = (widgetId, widgetSize) => {
    setSelectedWidgetId(widgetId);
    addWidgetToWorkspace({
      variables: {
        size: widgetSize,
        widgetId: widgetId,
        workspaceId: workspace.id,
      },
    }).then(({ data }) => {
      const {
        addWidgetToWorkspace: {
          id,
          position,
          size,
          widget: { name: title, componentName },
        },
      } = data;

      logger.log('WidgetLibrary:addWidgetToWorkspace', data);

      if (onWidgetInstall) {
        onWidgetInstall(id, position, size, title, componentName);
      }

      // onCloseClick();
    });
  };

  const handleRemoveClick = (widgetId) => {
    setSelectedWidgetId(widgetId);

    const workspaceWidgetId = workspaceWidgets.find(
      (ww) => ww.widget?.id === widgetId
    )!.id;

    logger.log(
      'WidgetLibrary:handleRemoveClick',
      workspace.id,
      widgetId,
      workspaceWidgetId
    );

    removeWidgetFromWorkspace({
      variables: {
        workspaceId: workspace.id,
        workspaceWidgetId,
      },
    })
      .then(
        ({
          data: { removeWidgetFromWorkspace: { status = '' } = {} } = {},
        }) => {
          logger.log('WidgetLibrary:removeWidget', status);
          if (onWidgetRemove) {
            onWidgetRemove(widgetId);
          }
          // onCloseClick();
        }
      )
      .catch((err) => {
        console.error(err);
      });
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(true);
  };

  const handleWidgetPreviewBackClick = () => {
    const indexInInstallable = installableWidgets.findIndex(
      (w) => w.id === selectedWidgetId
    );
    const indexInUninstallable = uninstallableWidgets.findIndex(
      (w) => w.id === selectedWidgetId
    );

    if (indexInInstallable > -1) {
      setSelectedWidgetId(
        indexInInstallable > 0
          ? installableWidgets[indexInInstallable - 1].id
          : uninstallableWidgets.length > 0
          ? uninstallableWidgets[uninstallableWidgets.length - 1].id
          : installableWidgets[0].id
      );
    } else if (indexInUninstallable > -1) {
      setSelectedWidgetId(
        indexInUninstallable > 0
          ? uninstallableWidgets[indexInUninstallable - 1].id
          : installableWidgets[installableWidgets.length - 1].id
      );
    }
  };

  const handleWidgetPreviewForwardClick = () => {
    const indexInInstallable = installableWidgets.findIndex(
      (w) => w.id === selectedWidgetId
    );
    const indexInUninstallable = uninstallableWidgets.findIndex(
      (w) => w.id === selectedWidgetId
    );

    if (indexInInstallable > -1) {
      setSelectedWidgetId(
        indexInInstallable < installableWidgets.length - 1
          ? installableWidgets[indexInInstallable + 1].id
          : uninstallableWidgets.length > 0
          ? uninstallableWidgets[0].id
          : installableWidgets[0].id
      );
    } else if (indexInUninstallable > -1) {
      setSelectedWidgetId(
        indexInUninstallable < uninstallableWidgets.length - 1
          ? uninstallableWidgets[indexInUninstallable + 1].id
          : installableWidgets[0].id
      );
    }
  };

  const installableWidgets = widgetsList.filter((w) =>
    w.membershipPlans.items.find(
      (m) => m.membershipPlan.id === user?.membership.membershipPlan.id
    )
  );

  const uninstallableWidgets = widgetsList.filter(
    (w) =>
      !w.membershipPlans.items.find(
        (m) => m.membershipPlan.id === user?.membership.membershipPlan.id
      )
  );

  return (
    <Modal
      isOpen={true}
      isFullScreenElement={true}
      className={'border-none'}
      handleMerlinLogo={true}
      onCloseClick={() => {
        if (onCloseClick) {
          onCloseClick();
        }
      }}
    >
      <>
        <Modal
          isOpen={isRequestWidgetOpen}
          height="auto"
          onCloseClick={() => setIsRequestWidgetOpen(false)}
        >
          <WidgetRequest />
        </Modal>

        <div className="flex w-full h-18 min-h-18 px-4 items-center justify-between border-b border-grey-10 bg-white shadow-md z-10 dark:bg-grey-90 dark:border-grey-80">
          <div className="flex w-32">
            <Icon svgName="merlinLogo" />
          </div>
          <h1 className="text-xl font-semibold">{t('widget.add.title')}</h1>
          <div
            className="flex items-center justify-center w-5 h-5 cursor-pointer"
            onClick={() => {
              onCloseClick();
            }}
          >
            <span className="material-icons text-xl">close</span>
          </div>
        </div>

        <div className="flex flex-col w-full h-full bg-white pb-4 pt-0 overflow-scroll dark:bg-black-real">
          <Loading
            className={'mx-auto my-8'}
            status={listWidgetsLoading || searchWidgetsLoading}
          />

          <>
            <div className="px-4 mb-4 flex flex-row items-center justify-between w-full border-b border-grey-10">
              <div className={'text-lg font-semibold'}>
                {widgetsList.length
                  ? t('widget.add.available')
                  : t('strategySearch.noResults')}
              </div>
              <div className="flex items-center bg-white dark:bg-grey-90">
                <div>
                  <SearchInput onChange={setValue} />
                </div>
                <div
                  className="flex items-center gap-2 font-medium py-4 px-6 pr-8 -mr-4 bg-grey-5 dark:bg-black cursor-pointer"
                  onClick={() =>
                    setIsRequestWidgetOpen((prevState) => !prevState)
                  }
                >
                  <Icon svgName={'request'} className="dark:text-white" />
                  {t('widget.add.requestWidget')}
                </div>
              </div>
            </div>
            <div className="px-4">
              <div className={'grid grid-cols-1 md:grid-cols-2 gap-6 w-full'}>
                {installableWidgets.map((widget: Widget) => (
                  <WidgetCard
                    key={widget.id}
                    title={widget.name}
                    description={widget.description}
                    iconUrl={widget.icon.size?.original}
                    category={widget.category}
                    suggestedSize={widget.suggestedSize}
                    viewMode={'compact'}
                    membershipPlan={
                      findFirstMembershipPlanForWidget(widget).membershipPlan
                        .title
                    }
                    isAvailable={true}
                    isLocked={widget.locked}
                    className="w-full"
                    onPreviewClick={() => {
                      setSelectedWidgetId(widget.id);
                      setIsWidgetPreviewVisible(true);
                    }}
                    onInstallClick={(selectedSize) => {
                      handleInstallClick(widget.id, selectedSize);
                    }}
                    onRemoveClick={() => {
                      handleRemoveClick(widget.id);
                    }}
                    isRemoveButtonLoading={
                      (getWorkspaceLoading || widgetRemoveLoading) &&
                      selectedWidgetId === widget.id
                    }
                    isInstallButtonLoading={
                      selectedWidgetId === widget.id &&
                      (getWorkspaceLoading || widgetAddLoading)
                    }
                    isInstalled={
                      !!workspaceWidgets.find(
                        (iw) => iw.widget?.id === widget.id
                      )
                    }
                  />
                ))}
              </div>

              {!!uninstallableWidgets.length && (
                <>
                  <div className={'text-lg font-semibold mt-12 mb-2'}>
                    {t('widget.add.unlock')}
                    <Icon
                      svgName={'lock'}
                      className={
                        'inline-flex bg-grey-10 text-grey-50 p-2 rounded-full h-8 w-8 mx-2'
                      }
                    />

                    <Button
                      variant={'primary'}
                      onClick={() => navigate('/subscriptions')}
                    >
                      {t('widget.add.upgradePlan')}
                    </Button>
                  </div>
                  <div className={'text-grey mb-4'}>
                    {t('widget.add.unlockSubtitle')}
                  </div>
                </>
              )}

              <div
                className={
                  'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 w-full'
                }
              >
                {uninstallableWidgets.map((widget: Widget, index) => (
                  <WidgetCard
                    key={widget.id}
                    title={widget.name}
                    description={widget.description}
                    iconUrl={widget.icon.size?.original}
                    category={widget.category}
                    viewMode={'full'}
                    membershipPlan={
                      findFirstMembershipPlanForWidget(widget).membershipPlan
                        .title
                    }
                    isAvailable={false}
                    className="w-full"
                    onPreviewClick={() => {
                      setSelectedWidgetId(widget.id);
                      setIsWidgetPreviewVisible(true);
                    }}
                    onUpgradeClick={() => {
                      setUpgradePlans(
                        widget.membershipPlans.items.map(
                          (m) => m.membershipPlan
                        )
                      );
                      setSelectedWidgetId(widget.id);
                      handleUpgradeClick();
                    }}
                    isInstalled={
                      !!workspaceWidgets.find(
                        (iw) => iw.widget?.id === widget.id
                      )
                    }
                  />
                ))}
              </div>
            </div>
          </>
        </div>
        <UpgradeSubscription
          isOpen={isUpgradeModalOpen}
          onCloseClick={() => {
            setIsUpgradeModalOpen(false);
          }}
          plans={upgradePlans as IMembershipPlan[]}
          feature={'WIDGET'}
          viewMode={'modal'}
        />

        {isWidgetPreviewVisible && (
          <WidgetPreview
            className="max-h-screen-50"
            customPaddings="p-0"
            widgetData={widgetsList.find((w) => w.id === selectedWidgetId)!}
            onCloseClick={() => setIsWidgetPreviewVisible(false)}
            onBackClick={handleWidgetPreviewBackClick}
            onForwardClick={handleWidgetPreviewForwardClick}
            onInstallClick={(selectedSize) =>
              handleInstallClick(selectedWidgetId, selectedSize)
            }
            isInstallButtonLoading={widgetAddLoading}
            isInstalled={
              !!workspaceWidgets.find(
                (iw) => iw.widget?.id === selectedWidgetId
              )
            }
            isAvailable={
              !uninstallableWidgets.find((w) => w.id === selectedWidgetId)
            }
          />
        )}
      </>
    </Modal>
  );
};
